// Reports.js
import React, { useEffect, useState } from "react";
import { AgGridReact } from "@ag-grid-community/react";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { ModuleRegistry } from "@ag-grid-community/core";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";
import { Eye, Download } from "lucide-react";
import Swal from "sweetalert2";

// register modules once
ModuleRegistry.registerModules([ClientSideRowModelModule]);

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [viewReport, setViewReport] = useState(null);

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("savedReports")) || [];
    setReports(saved);
  }, []);

  const handleDownload = (report) => {
    Swal.fire({
      title: "Download Report",
      text: "Select format to download this report:",
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Download CSV",
      cancelButtonText: "Download Excel",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        downloadAsCSV(report);
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        downloadAsExcel(report);
      }
    });
  };

  const downloadAsCSV = (report) => {
    if (!report.campaigns || report.campaigns.length === 0) return;
    const headers = Object.keys(report.campaigns[0]);
    const csvRows = [
      headers.join(","),
      ...report.campaigns.map((c) => headers.map((h) => c[h]).join(",")),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${report.name}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAsExcel = (report) => {
    if (!report.campaigns || report.campaigns.length === 0) return;
    const headers = Object.keys(report.campaigns[0]);
    const rows = report.campaigns.map((c) => headers.map((h) => c[h]));
    const worksheet = [
      headers.join("\t"),
      ...rows.map((r) => r.join("\t")),
    ].join("\n");
    const blob = new Blob([worksheet], {
      type: "application/vnd.ms-excel",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${report.name}.xls`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = [
    { headerName: "Name", field: "name", flex: 2 },
    { headerName: "Generated By", field: "generatedBy", flex: 2 },
    {
      headerName: "View",
      field: "view",
      flex: 1,
      cellRenderer: (params) => (
        <button
          className="reports-view-btn"
          onClick={() => setViewReport(params.data)}
        >
          <Eye size={18} color="#2f2fef" />
        </button>
      ),
    },
    {
      headerName: "Download",
      field: "download",
      flex: 1,
      cellRenderer: (params) => (
        <button
          className="reports-view-btn"
          onClick={() => handleDownload(params.data)}
        >
          <Download size={18} color="#2f2fef" />
        </button>
      ),
    },
  ];

  return (
    <div className="reports-page-container">
      <div className="ag-theme-quartz" style={{ height: 420, width: "100%" }}>
        <AgGridReact
          rowData={reports}
          columnDefs={columns}
          pagination={true}
          paginationPageSize={10}
        />
      </div>

      {viewReport && (
        <div
          className="reports-genrate-page-popup-overlay"
          onClick={() => setViewReport(null)}
        >
          <div
            className="reports-genrate-page-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="reports-genrate-page-popup-header">
              <h3>{viewReport.name}</h3>
              <button
                className="reports-genrate-page-popup-close"
                onClick={() => setViewReport(null)}
              >
                ✕
              </button>
            </div>

            <div className="reports-genrate-page-popup-body">
              <p>
                <strong>Generated By:</strong> {viewReport.generatedBy}
              </p>
              <p>
                <strong>Date:</strong> {viewReport.createdAt}
              </p>

              {viewReport.campaigns && (
                <table className="reports-genrate-page-popup-table">
                  <thead>
                    <tr>
                      <th>Campaign</th>
                      <th>Spend ($)</th>
                      <th>Budget ($)</th>
                      <th>Clicks</th>
                      <th>Conversions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewReport.campaigns.map((c, i) => (
                      <tr key={i}>
                        <td>{c.name}</td>
                        <td>{c.spend}</td>
                        <td>{c.budget}</td>
                        <td>{c.clicks}</td>
                        <td>{c.conversions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {viewReport.chartData ? (
                <img
                  src={viewReport.chartData}
                  alt="Report Chart"
                  className="reports-genrate-page-popup-chart"
                />
              ) : (
                <p>No chart data found for this report.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
